import { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import { Toaster } from '@/components/ui/sonner';
import { useTheme } from '@/components/ThemeProvider';
import { AlertTriangle, X, Menu } from 'lucide-react';

const ImpersonationBanner = ({ sidebarOpen }) => {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState(null);

  useEffect(() => {
    const checkImpersonation = () => {
      const impersonating = localStorage.getItem('impersonating') === 'true';
      const userData = localStorage.getItem('impersonated_user');
      setIsImpersonating(impersonating);
      if (userData) {
        try {
          setImpersonatedUser(JSON.parse(userData));
        } catch (e) {
          setImpersonatedUser(null);
        }
      }
    };
    checkImpersonation();
    window.addEventListener('storage', checkImpersonation);
    return () => window.removeEventListener('storage', checkImpersonation);
  }, []);

  const handleExitImpersonation = () => {
    const originalToken = localStorage.getItem('admin_original_token');
    if (originalToken) {
      localStorage.setItem('access_token', originalToken);
      localStorage.removeItem('admin_original_token');
      localStorage.removeItem('impersonating');
      localStorage.removeItem('impersonated_user');
      window.location.href = '/admin/usuarios';
    }
  };

  if (!isImpersonating) return null;

  return (
    <div className={`fixed top-0 right-0 z-50 bg-amber-500 text-black px-4 py-2 flex items-center justify-between left-0 lg:left-64`}>
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-5 h-5" />
        <span className="font-medium text-sm">
          Viendo como: <strong className="truncate max-w-[150px] inline-block align-middle">{impersonatedUser?.email}</strong>
        </span>
      </div>
      <button 
        onClick={handleExitImpersonation}
        className="flex items-center gap-1 bg-black/20 hover:bg-black/30 px-3 py-1 rounded-lg font-medium transition-colors text-sm"
      >
        <X className="w-4 h-4" />
        <span className="hidden sm:inline">Salir</span>
      </button>
    </div>
  );
};

const MainLayout = ({ children }) => {
  const { isDark } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isImpersonating = typeof window !== 'undefined' && localStorage.getItem('impersonating') === 'true';
  
  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sidebarOpen && window.innerWidth < 1024) {
        const sidebar = document.querySelector('[data-testid="sidebar"]');
        if (sidebar && !sidebar.contains(e.target) && !e.target.closest('[data-testid="mobile-menu-btn"]')) {
          setSidebarOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sidebarOpen]);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return (
    <div className="min-h-screen bg-background relative">
      {/* Impersonation Banner */}
      <ImpersonationBanner sidebarOpen={sidebarOpen} />
      
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white/80 dark:bg-[#0A0A12]/90 backdrop-blur-xl border-b border-periwinkle-300/30 dark:border-white/10">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-periwinkle-100/50 dark:hover:bg-white/10 transition-colors"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6 text-foreground" />
          </button>
          <img 
            src="/logo-dark.png"
            alt="FASCINAI ONE" 
            className={`h-8 object-contain ${isDark ? '' : 'brightness-[0.15] saturate-150'}`}
          />
          <div className="w-10" /> {/* Spacer for centering */}
        </div>
      </header>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Background Orbs - Adapt to theme */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {isDark ? (
          // Dark mode orbs
          <>
            <div 
              className="absolute w-[600px] h-[600px] -top-[200px] -right-[200px] rounded-full blur-[120px] bg-brand-500/30 animate-float-slow"
            />
            <div 
              className="absolute w-[500px] h-[500px] -bottom-[150px] -left-[150px] rounded-full blur-[120px] bg-lavender/20 animate-float-slower"
              style={{ animationDelay: '2s' }}
            />
            <div 
              className="absolute w-[300px] h-[300px] top-1/3 left-1/2 rounded-full blur-[100px] bg-lime/10 animate-float-slow"
              style={{ animationDelay: '4s' }}
            />
          </>
        ) : (
          // Light mode orbs
          <>
            <div 
              className="absolute w-[600px] h-[600px] -top-[200px] -right-[200px] rounded-full blur-[120px] bg-periwinkle-400/20"
            />
            <div 
              className="absolute w-[500px] h-[500px] -bottom-[150px] -left-[150px] rounded-full blur-[120px] bg-brand-500/10"
            />
            <div 
              className="absolute w-[300px] h-[300px] top-1/3 left-1/2 rounded-full blur-[100px] bg-lavender/10"
            />
          </>
        )}
      </div>

      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <main className={`lg:ml-64 min-h-screen relative z-10 pt-16 lg:pt-0 pb-8 ${isImpersonating ? 'lg:pt-10' : ''}`}>
        <div className="p-4 sm:p-6 lg:p-8 pb-16">
          {children}
        </div>
      </main>
      <Toaster />
    </div>
  );
};

export default MainLayout;
